import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { FaFileImport, FaPlus, FaTrash } from "react-icons/fa";
import { crm, Pill } from "../../../theme";
import {
  BillingSetting,
  BillingSettingException,
  BillingSettingKind,
  billingKindLabels,
  FiscalRates,
} from "../../../models/BillingSetting";
import { BRAZIL_STATES } from "../../../models/CommercialClient";
import {
  getBillingSetting,
  getBillingSettings,
  importGettBillingSettings,
  saveBillingSetting,
} from "../../../services/billingSettingService";
import { toneColors } from "../../process/components/duimp";

const UNIT_NAMES: Record<number, string> = { 1: "CVF Logística", 2: "CVF Distribuidora", 3: "CVF Rondônia", 4: "W Trading" };

type RateKey = keyof FiscalRates;
const TEXT_KEYS: RateKey[] = ["cfop", "cstIcms", "presumedCreditCode", "cestCode"];

/** Campos da parametrização (ordem da tela do Gett). */
const RATE_FIELDS: [RateKey, string][] = [
  ["cfop", "CFOP"],
  ["cstIcms", "CST ICMS"],
  ["icmsPercent", "ICMS %"],
  ["icmsBaseReductionPercent", "Red. BC ICMS %"],
  ["presumedCreditCode", "Benefício (cód.)"],
  ["presumedCreditPercent", "Crédito presumido %"],
  ["stInternalPercent", "ICMS ST (interna) %"],
  ["mvaPercent", "MVA %"],
  ["stBaseReductionPercent", "Red. BC ST %"],
  ["fcpPercent", "FCP %"],
  ["fcpStPercent", "FCP ST %"],
  ["ipiPercent", "IPI %"],
  ["pisPercent", "PIS %"],
  ["cofinsPercent", "COFINS %"],
  ["difalInternalPercent", "DIFAL interna destino %"],
  ["difalInterstatePercent", "DIFAL interestadual %"],
  ["difalFcpPercent", "DIFAL FCP destino %"],
  ["ibsStatePercent", "IBS UF %"],
  ["ibsCityPercent", "IBS município %"],
  ["cbsPercent", "CBS %"],
  ["cestCode", "CEST"],
];

/** Colunas da tabela de exceções (as que mudam no Gett). */
const EXCEPTION_FIELDS: [RateKey, string][] = [
  ["cfop", "CFOP"],
  ["icmsPercent", "ICMS"],
  ["icmsBaseReductionPercent", "Red. BC"],
  ["presumedCreditPercent", "Cred. pres."],
  ["stInternalPercent", "ST int."],
  ["mvaPercent", "MVA"],
  ["stBaseReductionPercent", "Red. BC ST"],
  ["ipiPercent", "IPI"],
  ["pisPercent", "PIS"],
  ["cofinsPercent", "COFINS"],
];

type RatesDraft = Partial<Record<RateKey, string>>;

const toText = (value: unknown) => (value == null ? "" : typeof value === "number" ? String(value).replace(".", ",") : String(value));
const ratesToDraft = (rates?: FiscalRates | null): RatesDraft =>
  Object.fromEntries(RATE_FIELDS.map(([key]) => [key, toText(rates?.[key])])) as RatesDraft;

const parse = (value?: string) => {
  const text = (value ?? "").trim();
  if (!text) return null;
  const normalized = text.includes(",") ? text.replace(/\./g, "").replace(",", ".") : text;
  const n = Number(normalized);
  return Number.isNaN(n) ? NaN : n;
};

const draftToRates = (draft: RatesDraft): { rates: FiscalRates; invalid: string | null } => {
  const rates: FiscalRates = {};
  let invalid: string | null = null;
  RATE_FIELDS.forEach(([key, label]) => {
    if (TEXT_KEYS.includes(key)) {
      (rates as any)[key] = draft[key]?.trim() || null;
      return;
    }
    const n = parse(draft[key]);
    if (Number.isNaN(n)) invalid = invalid ?? `${label}: número inválido`;
    (rates as any)[key] = Number.isNaN(n) ? null : n;
  });
  return { rates, invalid };
};

interface ExceptionDraft {
  key: number;
  id?: string;
  ncm: string;
  state: string;
  customerName: string;
  productDescription: string;
  rates: RatesDraft;
}

let nextKey = 1;
const exceptionToDraft = (e: BillingSettingException): ExceptionDraft => ({
  key: nextKey++,
  id: e.id,
  ncm: e.ncm ?? "",
  state: e.state ?? "",
  customerName: e.customerName ?? "",
  productDescription: e.productDescription ?? "",
  rates: ratesToDraft(e.rates),
});

interface Draft {
  setting: BillingSetting;
  rates: RatesDraft;
  exceptions: ExceptionDraft[];
}

const percent = (value?: number | null) => (value == null ? "—" : `${value.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}%`);

/**
 * Parametrizações do faturamento no modelo do Gett (natureza de operação + alíquotas + exceções por NCM/UF/cliente),
 * usadas pelos simuladores. Importa o JSON exportado do Gett (reimportar atualiza pelo id do Gett).
 */
const BillingSettings = () => {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [list, setList] = useState<BillingSetting[] | null>(null);
  const [kind, setKind] = useState<BillingSettingKind | "">("encomenda");
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    getBillingSettings()
      .then(setList)
      .catch(() => {
        setList([]);
        toast({ status: "error", title: "Não foi possível carregar as parametrizações" });
      });

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (list ?? []).filter(
      (s) => (!kind || s.kind === kind) && (!term || s.name.toLowerCase().includes(term) || (s.clientName ?? "").toLowerCase().includes(term)),
    );
  }, [list, kind, search]);

  const importFile = async (file?: File | null) => {
    if (!file) return;
    setImporting(true);
    try {
      const r = await importGettBillingSettings(file);
      toast({
        status: "success",
        title: "Importado do Gett",
        description: `${r.created} novas, ${r.updated} atualizadas, ${r.exceptions} exceções, ${r.linked} ligadas a clientes.`,
      });
      await load();
    } catch (error: any) {
      toast({ status: "error", title: error?.response?.data?.notifications?.[0] ?? "Não foi possível importar o arquivo" });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const open = async (setting?: BillingSetting) => {
    if (!setting) {
      setDraft({
        setting: { name: "", kind: "encomenda", isEndCustomer: false, exceptionCount: 0, rates: {}, unitType: 2, unitState: "SC" },
        rates: ratesToDraft({}),
        exceptions: [],
      });
      return;
    }
    setLoadingId(setting.id!);
    try {
      const full = await getBillingSetting(setting.id!);
      setDraft({ setting: full, rates: ratesToDraft(full.rates), exceptions: (full.exceptions ?? []).map(exceptionToDraft) });
    } catch {
      toast({ status: "error", title: "Não foi possível abrir a parametrização" });
    } finally {
      setLoadingId(null);
    }
  };

  const patchSetting = (patch: Partial<BillingSetting>) => setDraft((d) => (d ? { ...d, setting: { ...d.setting, ...patch } } : d));
  const patchException = (key: number, patch: Partial<ExceptionDraft>) =>
    setDraft((d) => (d ? { ...d, exceptions: d.exceptions.map((e) => (e.key === key ? { ...e, ...patch } : e)) } : d));

  const save = async () => {
    if (!draft) return;
    const base = draftToRates(draft.rates);
    if (base.invalid) return toast({ status: "error", title: base.invalid });
    const exceptions: BillingSettingException[] = [];
    for (let index = 0; index < draft.exceptions.length; index++) {
      const e = draft.exceptions[index];
      // Exceção só sobrescreve os campos preenchidos: começa vazia e aplica os da tabela
      const parsed = draftToRates(e.rates);
      if (parsed.invalid) return toast({ status: "error", title: `Exceção ${index + 1}: ${parsed.invalid}` });
      exceptions.push({
        id: e.id,
        ncm: e.ncm.replace(/\D/g, "") || null,
        state: e.state || null,
        customerName: e.customerName.trim() || null,
        productDescription: e.productDescription.trim() || null,
        rates: parsed.rates,
      });
    }
    setSaving(true);
    try {
      const result = await saveBillingSetting({ ...draft.setting, rates: base.rates, exceptions });
      if (!result.success) {
        toast({ status: "error", title: result.notifications?.[0] ?? "Não foi possível salvar" });
        return;
      }
      toast({ status: "success", title: "Parametrização salva" });
      setDraft(null);
      await load();
    } catch {
      toast({ status: "error", title: "Não foi possível salvar" });
    } finally {
      setSaving(false);
    }
  };

  const s = draft?.setting;

  return (
    <Box w="100%">
      <Flex justify="space-between" align="flex-start" gap="12px" wrap="wrap">
        <Box>
          <Text fontSize="20px" fontWeight="700" color={crm.text}>Parametrizações fiscais</Text>
          <Text color={crm.textMuted} fontSize="sm" mt="4px" maxW="720px">
            Modelo do Gett (Faturamento › Parametrizações): alíquotas por operação e exceções por NCM, UF do cliente e cliente.
            Os simuladores de Encomenda e Conta e ordem usam essas alíquotas. Reimportar o arquivo do Gett sobrescreve as alíquotas
            das parametrizações vindas de lá (o vínculo com o cliente é mantido).
          </Text>
        </Box>
        <Flex gap="8px">
          <input ref={fileRef} type="file" accept="application/json,.json" style={{ display: "none" }} onChange={(e) => importFile(e.target.files?.[0])} />
          <Button size="sm" leftIcon={<FaFileImport />} variant="outline" isLoading={importing} onClick={() => fileRef.current?.click()}>
            Importar JSON do Gett
          </Button>
          <Button size="sm" leftIcon={<FaPlus />} bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} onClick={() => open()}>
            Nova
          </Button>
        </Flex>
      </Flex>

      <Flex gap="8px" mt="16px" mb="12px" wrap="wrap">
        <Select size="sm" w="190px" value={kind} onChange={(e) => setKind(e.target.value as BillingSettingKind | "")}>
          <option value="">Todos os tipos</option>
          {(Object.keys(billingKindLabels) as BillingSettingKind[]).map((k) => (
            <option key={k} value={k}>{billingKindLabels[k]}</option>
          ))}
        </Select>
        <Input size="sm" maxW="320px" placeholder="Buscar por nome ou cliente" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Text fontSize="sm" color={crm.textMuted} alignSelf="center">{list ? `${filtered.length} de ${list.length}` : ""}</Text>
      </Flex>

      {list == null ? (
        <Flex justify="center" py="32px"><Spinner /></Flex>
      ) : list.length === 0 ? (
        <Text color={crm.textMuted} fontSize="sm" py="20px">
          Nenhuma parametrização. Use "Importar JSON do Gett" com o arquivo exportado das parametrizações.
        </Text>
      ) : (
        <Box overflowX="auto" border={`1px solid ${crm.border}`} borderRadius="12px">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Nome</Th>
                <Th>Tipo</Th>
                <Th>Unidade</Th>
                <Th>CFOP</Th>
                <Th isNumeric>ICMS</Th>
                <Th isNumeric>Exceções</Th>
                <Th>Cliente</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((item) => (
                <Tr key={item.id} cursor="pointer" _hover={{ bg: crm.subtle }} onClick={() => open(item)}>
                  <Td maxW="380px">
                    <Flex align="center" gap="6px">
                      {loadingId === item.id && <Spinner size="xs" />}
                      <Text noOfLines={1} title={item.name}>{item.name}</Text>
                    </Flex>
                  </Td>
                  <Td><Pill {...toneColors.neutral}>{billingKindLabels[item.kind]}</Pill></Td>
                  <Td whiteSpace="nowrap">{item.unitType ? UNIT_NAMES[item.unitType] : "—"}{item.unitState ? ` · ${item.unitState}` : ""}</Td>
                  <Td>{item.rates?.cfop ?? "—"}</Td>
                  <Td isNumeric>{percent(item.rates?.icmsPercent)}</Td>
                  <Td isNumeric>{item.exceptionCount || "—"}</Td>
                  <Td maxW="220px"><Text noOfLines={1} color={item.clientName ? crm.text : crm.textFaint}>{item.clientName ?? "Sem cliente"}</Text></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={!!draft} onClose={() => setDraft(null)} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{s?.id ? "Parametrização" : "Nova parametrização"}{s?.gettId ? <Text as="span" fontSize="sm" color={crm.textMuted} fontWeight="400"> · Gett #{s.gettId}</Text> : null}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {draft && s && (
              <>
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing="10px">
                  <Box gridColumn={{ md: "span 2" }}>
                    <Text fontSize="12px" color={crm.textMuted} mb="4px">Nome</Text>
                    <Input size="sm" maxLength={300} value={s.name} onChange={(e) => patchSetting({ name: e.target.value })} />
                  </Box>
                  <Box>
                    <Text fontSize="12px" color={crm.textMuted} mb="4px">Tipo</Text>
                    <Select size="sm" value={s.kind} onChange={(e) => patchSetting({ kind: e.target.value as BillingSettingKind })}>
                      {(Object.keys(billingKindLabels) as BillingSettingKind[]).map((k) => <option key={k} value={k}>{billingKindLabels[k]}</option>)}
                    </Select>
                  </Box>
                  <Box>
                    <Text fontSize="12px" color={crm.textMuted} mb="4px">Unidade emissora</Text>
                    <Flex gap="6px">
                      <Select size="sm" value={s.unitType ?? ""} onChange={(e) => patchSetting({ unitType: e.target.value ? Number(e.target.value) : null })}>
                        <option value="">—</option>
                        {Object.entries(UNIT_NAMES).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                      </Select>
                      <Select size="sm" w="80px" value={s.unitState ?? ""} onChange={(e) => patchSetting({ unitState: e.target.value || null })}>
                        <option value="">UF</option>
                        {BRAZIL_STATES.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                      </Select>
                    </Flex>
                  </Box>
                  <Box gridColumn={{ md: "span 2" }}>
                    <Text fontSize="12px" color={crm.textMuted} mb="4px">Natureza de operação</Text>
                    <Input size="sm" maxLength={200} value={s.natureOperation ?? ""} onChange={(e) => patchSetting({ natureOperation: e.target.value })} />
                  </Box>
                  <Box>
                    <Text fontSize="12px" color={crm.textMuted} mb="4px">Cliente ligado</Text>
                    <Text fontSize="sm" pt="6px" color={s.clientName ? crm.text : crm.textFaint}>{s.clientName ?? "Ligue em Clientes Ativos"}</Text>
                  </Box>
                  <Flex align="flex-end" pb="6px">
                    <Checkbox size="sm" isChecked={s.isEndCustomer} onChange={(e) => patchSetting({ isEndCustomer: e.target.checked })}>Consumidor final</Checkbox>
                  </Flex>
                </SimpleGrid>

                <Text fontWeight="700" mt="18px" mb="8px">Alíquotas</Text>
                <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing="10px">
                  {RATE_FIELDS.map(([key, label]) => (
                    <Box key={key}>
                      <Text fontSize="11px" color={crm.textMuted} mb="2px">{label}</Text>
                      <Input size="sm" value={draft.rates[key] ?? ""} onChange={(e) => setDraft({ ...draft, rates: { ...draft.rates, [key]: e.target.value } })} />
                    </Box>
                  ))}
                </SimpleGrid>

                <Box mt="10px">
                  <Text fontSize="12px" color={crm.textMuted} mb="4px">Informações ao fisco</Text>
                  <Textarea size="sm" rows={2} maxLength={2000} value={s.notes ?? ""} onChange={(e) => patchSetting({ notes: e.target.value })} />
                </Box>

                <Flex justify="space-between" align="center" mt="20px" mb="8px">
                  <Box>
                    <Text fontWeight="700">Exceções ({draft.exceptions.length})</Text>
                    <Text fontSize="xs" color={crm.textMuted}>
                      Vale a mais específica que bater com o item (cliente &gt; UF &gt; NCM). Campo vazio = usa o da parametrização.
                      Exceção por produto do Gett fica guardada, mas o simulador não aplica.
                    </Text>
                  </Box>
                  <Button size="xs" leftIcon={<FaPlus />} variant="outline" onClick={() => setDraft({ ...draft, exceptions: [...draft.exceptions, exceptionToDraft({ rates: {} })] })}>
                    Exceção
                  </Button>
                </Flex>
                <Box overflowX="auto" border={`1px solid ${crm.border}`} borderRadius="10px">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>NCM</Th>
                        <Th>UF</Th>
                        <Th>Cliente</Th>
                        <Th>Produto</Th>
                        {EXCEPTION_FIELDS.map(([key, label]) => <Th key={key}>{label}</Th>)}
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {draft.exceptions.map((e) => (
                        <Tr key={e.key}>
                          <Td><Input size="xs" w="96px" value={e.ncm} placeholder="00000000" onChange={(ev) => patchException(e.key, { ncm: ev.target.value })} /></Td>
                          <Td>
                            <Select size="xs" w="64px" value={e.state} onChange={(ev) => patchException(e.key, { state: ev.target.value })}>
                              <option value="">—</option>
                              {BRAZIL_STATES.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                            </Select>
                          </Td>
                          <Td><Input size="xs" w="150px" value={e.customerName} onChange={(ev) => patchException(e.key, { customerName: ev.target.value })} /></Td>
                          <Td><Input size="xs" w="150px" value={e.productDescription} onChange={(ev) => patchException(e.key, { productDescription: ev.target.value })} /></Td>
                          {EXCEPTION_FIELDS.map(([key]) => (
                            <Td key={key}>
                              <Input size="xs" w={key === "cfop" ? "64px" : "58px"} value={e.rates[key] ?? ""} onChange={(ev) => patchException(e.key, { rates: { ...e.rates, [key]: ev.target.value } })} />
                            </Td>
                          ))}
                          <Td>
                            <IconButton size="xs" variant="ghost" color={crm.danger} aria-label="Remover exceção" icon={<FaTrash />}
                              onClick={() => setDraft({ ...draft, exceptions: draft.exceptions.filter((x) => x.key !== e.key) })} />
                          </Td>
                        </Tr>
                      ))}
                      {draft.exceptions.length === 0 && (
                        <Tr><Td colSpan={EXCEPTION_FIELDS.length + 5} color={crm.textFaint}>Sem exceções.</Td></Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="8px" onClick={() => setDraft(null)}>Cancelar</Button>
            <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={saving} onClick={save}>Salvar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BillingSettings;
